import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { contentAPI, dashboardAPI } from '../services/api';

const ACCEPTED_TYPES = '.jpg,.jpeg,.png,.gif,.webp,.mp4,.webm';
const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB

const statusColor = (status) => {
  if (status === 'approved') return 'success';
  if (status === 'rejected') return 'error';
  return 'warning';
};

const UploadContent = () => {
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [editDuration, setEditDuration] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchLocations = useCallback(async () => {
    try {
      setLoadingLocations(true);
      const response = await dashboardAPI.getMyLocations();
      const locs = response.data?.data?.locations || [];
      setLocations(locs);
      if (locs.length === 1) {
        setSelectedLocationId(locs[0]._id);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error('Failed to load your locations');
    } finally {
      setLoadingLocations(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      setLoadingHistory(true);
      const response = await contentAPI.getMyContent();
      const content = response.data?.data?.content || [];
      setHistory(content);
    } catch (error) {
      console.error('Error fetching content history:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
    fetchHistory();
  }, [fetchLocations, fetchHistory]);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    if (selected.size > MAX_FILE_SIZE) {
      toast.error('File is too large. Maximum size is 500MB.');
      e.target.value = '';
      return;
    }

    setFile(selected);
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!selectedLocationId) {
      toast.error('Please select a location');
      return;
    }

    if (!file) {
      toast.error('Please choose a file to upload');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('locationId', selectedLocationId);

      const response = await contentAPI.upload(formData);
      const screenCount = response.data?.data?.screenCount || 1;

      toast.success(
        screenCount > 1
          ? `Content uploaded to ${screenCount} screens successfully`
          : 'Content uploaded successfully'
      );

      setFile(null);
      const fileInput = document.getElementById('content-file-input');
      if (fileInput) fileInput.value = '';

      fetchHistory();
    } catch (error) {
      console.error('Error uploading content:', error);
      const message = error.response?.data?.message || 'Failed to upload content';
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setEditNotes(item.notes || '');
    setEditDuration(item.displayDuration ?? '');
  };

  const closeEdit = () => {
    setEditItem(null);
    setEditNotes('');
    setEditDuration('');
  };

  const handleSaveEdit = async () => {
    if (!editItem) return;
    try {
      setSaving(true);
      await contentAPI.update(editItem._id, {
        notes: editNotes,
        displayDuration: editDuration === '' ? undefined : Number(editDuration),
      });
      toast.success('Content updated successfully');
      closeEdit();
      fetchHistory();
    } catch (error) {
      console.error('Error updating content:', error);
      const message = error.response?.data?.message || 'Failed to update content';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    const confirmed = window.confirm(`Delete "${item.filename}"? This cannot be undone.`);
    if (!confirmed) return;

    try {
      setDeletingId(item._id);
      await contentAPI.delete(item._id);
      toast.success('Content deleted successfully');
      setHistory((prev) => prev.filter((h) => h._id !== item._id));
    } catch (error) {
      console.error('Error deleting content:', error);
      const message = error.response?.data?.message || 'Failed to delete content';
      toast.error(message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', mb: 3 }}>
        Upload Content
      </Typography>

      <Paper sx={{ p: 3, borderRadius: '12px', border: '1px solid #e2e8f0', mb: 4 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          New Upload
        </Typography>

        {locations.length === 0 && !loadingLocations && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No locations are currently assigned to you. Please contact your administrator.
          </Alert>
        )}

        <Box component="form" onSubmit={handleUpload} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth disabled={loadingLocations || locations.length === 0}>
            <InputLabel id="location-select-label">Location</InputLabel>
            <Select
              labelId="location-select-label"
              value={selectedLocationId}
              label="Location"
              onChange={(e) => setSelectedLocationId(e.target.value)}
            >
              {locations.map((loc) => (
                <MenuItem key={loc._id} value={loc._id}>
                  {loc.locationName} {loc.city ? `- ${loc.city}` : ''} ({loc.screens.length} screen
                  {loc.screens.length !== 1 ? 's' : ''})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
              {file ? file.name : 'Choose file (image or video)'}
              <input
                id="content-file-input"
                type="file"
                hidden
                accept={ACCEPTED_TYPES}
                onChange={handleFileChange}
              />
            </Button>
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#64748b' }}>
              Accepted: JPG, PNG, GIF, WebP, MP4, WebM. Max size 500MB.
            </Typography>
          </Box>

          <Button
            type="submit"
            variant="contained"
            disabled={uploading || !file || !selectedLocationId}
            sx={{
              alignSelf: 'flex-start',
              backgroundColor: '#F7941D',
              '&:hover': { backgroundColor: '#C57617' },
            }}
          >
            {uploading ? <CircularProgress size={22} color="inherit" /> : 'Upload'}
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <Box
          sx={{
            p: 2.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #e2e8f0',
            backgroundColor: '#f8f9fb',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Upload History
          </Typography>
          <Button size="small" startIcon={<RefreshIcon />} onClick={fetchHistory}>
            Refresh
          </Button>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>File</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Screen</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Uploaded</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingHistory ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : history.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3, color: '#64748b' }}>
                    No content uploaded yet.
                  </TableCell>
                </TableRow>
              ) : (
                history.map((item) => (
                  <TableRow key={item._id}>
                    <TableCell>{item.filename}</TableCell>
                    <TableCell>{item.fileType}</TableCell>
                    <TableCell>{item.screen?.screenName || '-'}</TableCell>
                    <TableCell>
                      <Chip label={item.status} size="small" color={statusColor(item.status)} />
                    </TableCell>
                    <TableCell>
                      {item.uploadedAt ? new Date(item.uploadedAt).toLocaleString() : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(item)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          disabled={deletingId === item._id}
                          onClick={() => handleDelete(item)}
                        >
                          {deletingId === item._id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={Boolean(editItem)} onClose={closeEdit} fullWidth maxWidth="sm">
        <DialogTitle>Edit Content</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              {editItem?.filename}
            </Typography>
            <TextField
              label="Display Duration (seconds)"
              type="number"
              value={editDuration}
              onChange={(e) => setEditDuration(e.target.value)}
              inputProps={{ min: 1 }}
              helperText="How long this content stays on screen (images only)."
              fullWidth
            />
            <TextField
              label="Notes"
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={saving}
            sx={{ backgroundColor: '#F7941D', '&:hover': { backgroundColor: '#C57617' } }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UploadContent;
